import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Spin, Card, Tag, Typography, Empty, Row, Col, Input, Layout, Space } from 'antd';
import { getUserAssignedAudits } from '../../services/auditAPI';
import { setLoading, setUserAudits, setError } from '../../redux/slices/userAuditSlice';
import { FileText, Search, CheckCircle, Clock, Timer, Hand } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { Content } = Layout;
const { Search: AntdSearch } = Input;

const UserDashboard = () => {
  const dispatch = useDispatch();
  const { userAudits, loading, error } = useSelector((state) => state.userAudit);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchAssignedAudits();
  }, []);

  const fetchAssignedAudits = async () => {
    dispatch(setLoading(true));
    try {
      const audits = await getUserAssignedAudits();
      dispatch(setUserAudits(audits));
    } catch (err) {
      dispatch(setError(err.message));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleStartAudit = (auditId) => {
    navigate(`/audit/${auditId}`);
  };

  const getStatusProps = (status) => {
    switch (status) {
      case 'Completed':
        return {
          color: '#28a745', // Green
          icon: <CheckCircle size={16} />,
        };
      case 'InProgress':
        return {
          color: '#007bff', // Blue
          icon: <Timer size={16} />,
        };
      case 'Pending':
        return {
          color: '#ffc107', // Yellow
          icon: <Clock size={16} />,
        };
      default:
        return {
          color: '#6c757d', // Gray
          icon: <FileText size={16} />,
        };
    }
  };

  const getButtonText = (status) => {
    switch (status) {
      case 'Completed':
        return 'View Audit';
      case 'Pending':
        return 'Start Audit';
      case 'InProgress':
        return 'Resume Audit';
      default:
        return 'View Audit';
    }
  };

  const filteredAudits = userAudits.filter((audit) =>
    audit.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    audit.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    audit.status.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" tip="Loading assigned tasks..." />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Title level={4} type="danger">An error occurred</Title>
        <Text type="danger">{error}</Text>
      </div>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: '#ffffff' }}>
      <Content className='p-4'>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '32px',
          flexWrap: 'wrap',
          gap: '16px',
        }}>
          <Title level={2} style={{ margin: 0, fontWeight: 700 }}>
            Your Assigned Tasks
          </Title>
          <AntdSearch
            placeholder="Search tasks by title or status..."
            allowClear
            style={{ width: '100%', maxWidth: '400px' }}
            onChange={(e) => setSearchTerm(e.target.value)}
            enterButton={<Button type="primary" icon={<Search size={18} />} />}
          />
        </div>

        {filteredAudits.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Text type="secondary">
                No tasks assigned to you.
              </Text>
            }
          />
        ) : (
          <Row gutter={[24, 24]}>
            {filteredAudits.map((audit) => {
              const statusProps = getStatusProps(audit.status);
              return (
                <Col xs={24} sm={12} md={8} lg={6} key={audit._id}>
                  <Card
                    hoverable
                    style={{
                      borderRadius: '12px',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      border: 'none',
                      height: '300px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      overflow: 'hidden',
                    }}
                    bodyStyle={{ padding: '24px', display: 'flex', flexDirection: 'column', height: '100%' }}
                  >
                    <Space direction="vertical" style={{ width: '100%', flexGrow: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                        <Title level={4} style={{ margin: 0, color: '#333', lineHeight: 1.4, wordBreak: 'break-word', whiteSpace: 'normal' }}>
                          {audit.title}
                        </Title>
                        <Tag
                          color={statusProps.color}
                          style={{
                            border: '1px solid',
                            borderColor: statusProps.color,
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            padding: '4px 8px',
                            fontWeight: 'bold',
                            borderRadius: '20px',
                            whiteSpace: 'nowrap',
                            display: 'flex',
                            color: '#000000',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          {statusProps.icon}
                          {audit.status}
                        </Tag>
                      </div>
                      <Text type="secondary" style={{ display: 'block', minHeight: '40px', lineHeight: 1.5, wordBreak: 'break-word', whiteSpace: 'normal', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {audit.description || 'No description provided.'}
                      </Text>
                    </Space>
                    <div style={{ marginTop: '24px' }}>
                      <Button
                        type={audit.status === 'Completed' ? 'default' : 'primary'}
                        onClick={() => handleStartAudit(audit._id)}
                        style={{ width: '100%', borderRadius: '6px' }}
                      >
                        {getButtonText(audit.status)}
                      </Button>
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </Content>
    </Layout>
  );
};

export default UserDashboard;